/**
 * TradeModal Component
 *
 * Modal for initiating and managing creature trades.
 * Step 1: Select creature you want from target user
 * Step 2: Select your creature to offer
 * Step 3: Confirm and send trade offer
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTradeNotifications, useTradeCountdown } from '../hooks/useTradeNotifications';
import type { TradeNotification } from '@drawn-of-war/shared';
import styles from './TradeModal.module.css';

/**
 * Creature type for selection
 */
interface TradeableCreature {
  id: string;
  name: string;
  concept: string;
  menuSprite: string;
  isTradeable: boolean;
  tradeableReason?: string;
}

/**
 * Props for TradeModal
 */
interface TradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Target user to trade with */
  targetUserId: string;
  targetDisplayName: string;
}

type TradeStep = 'select-theirs' | 'select-yours' | 'confirm' | 'existing';

/**
 * TradeModal Component
 */
export function TradeModal({
  isOpen,
  onClose,
  targetUserId,
  targetDisplayName
}: TradeModalProps) {
  const { getIdToken } = useAuth();
  const {
    trades,
    createTrade,
    acceptTrade,
    declineTrade,
    counterTrade,
    isLoading: tradeActionLoading,
    error: tradeError,
    clearError
  } = useTradeNotifications();

  // Their creatures (target user)
  const [theirCreatures, setTheirCreatures] = useState<TradeableCreature[]>([]);
  const [loadingTheirs, setLoadingTheirs] = useState(true);

  // Your creatures
  const [yourCreatures, setYourCreatures] = useState<TradeableCreature[]>([]);
  const [loadingYours, setLoadingYours] = useState(false);

  // Selected creatures
  const [selectedTheirCreature, setSelectedTheirCreature] = useState<TradeableCreature | null>(null);
  const [selectedYourCreature, setSelectedYourCreature] = useState<TradeableCreature | null>(null);

  const [step, setStep] = useState<TradeStep>('select-theirs');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check if there's an existing trade with this user
  const existingTrade = trades.find(
    t => t.withUser.id === targetUserId
  );

  // Fetch target user's creatures
  const fetchTheirCreatures = useCallback(async () => {
    setLoadingTheirs(true);
    setError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/users/${targetUserId}/creatures?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load their creatures');
      }

      const data = await response.json();

      const creatures: TradeableCreature[] = (data.creatures || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        concept: c.concept || '',
        menuSprite: c.menuSprite || '',
        isTradeable: true,
        tradeableReason: undefined
      }));

      setTheirCreatures(creatures);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load creatures');
    } finally {
      setLoadingTheirs(false);
    }
  }, [getIdToken, targetUserId]);

  // Fetch your creatures
  const fetchYourCreatures = useCallback(async () => {
    setLoadingYours(true);
    setError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/creature-library/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load your creatures');
      }

      const data = await response.json();

      const creatures: TradeableCreature[] = data.creatures.map((c: any) => ({
        id: c.id,
        name: c.name,
        concept: c.concept || '',
        menuSprite: c.sprites?.menuSprite || '',
        isTradeable: true,
        tradeableReason: undefined
      }));

      setYourCreatures(creatures);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load creatures');
    } finally {
      setLoadingYours(false);
    }
  }, [getIdToken]);

  // Effect: Initialize when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTheirCreature(null);
      setSelectedYourCreature(null);
      setSuccess(null);
      setError(null);
      clearError();

      // Check if there's an existing trade
      if (existingTrade) {
        setStep('existing');
      } else {
        setStep('select-theirs');
        fetchTheirCreatures();
      }
    }
  }, [isOpen, clearError, existingTrade, fetchTheirCreatures]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle selecting their creature
  const handleSelectTheirCreature = (creature: TradeableCreature) => {
    if (!creature.isTradeable) return;
    setSelectedTheirCreature(creature);
    setStep('select-yours');
    fetchYourCreatures();
  };

  // Handle selecting your creature
  const handleSelectYourCreature = (creature: TradeableCreature) => {
    if (!creature.isTradeable) return;
    setSelectedYourCreature(creature);
    setStep('confirm');
  };

  // Handle trade creation
  const handleConfirmTrade = async () => {
    if (!selectedTheirCreature || !selectedYourCreature) return;

    try {
      await createTrade(targetUserId, selectedTheirCreature.id, selectedYourCreature.id);
      setSuccess(`Trade offer sent to ${targetDisplayName}!`);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch {
      // Error already handled in hook
    }
  };

  // Handle accept trade
  const handleAcceptTrade = async (tradeId: string) => {
    await acceptTrade(tradeId);
    setSuccess('Trade completed!');
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  // Handle decline trade
  const handleDeclineTrade = async (tradeId: string) => {
    await declineTrade(tradeId);
    onClose();
  };

  // Handle counter trade - go to select their creature first
  const handleStartCounter = () => {
    setSelectedTheirCreature(null);
    setSelectedYourCreature(null);
    setStep('select-theirs');
    fetchTheirCreatures();
  };

  // Handle counter trade submission
  const handleCounterTrade = async (tradeId: string) => {
    if (!selectedYourCreature) return;
    await counterTrade(tradeId, selectedYourCreature.id);
    setSuccess('Counter-offer sent!');
    setTimeout(() => {
      setStep('existing');
      setSelectedTheirCreature(null);
      setSelectedYourCreature(null);
    }, 1500);
  };

  if (!isOpen) return null;

  const displayError = error || tradeError;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
          ✕
        </button>

        {/* Header */}
        <header className={styles.header}>
          <h2>Trade with {targetDisplayName}</h2>
        </header>

        {/* Progress indicator */}
        {step !== 'existing' && (
          <div className={styles.progressBar}>
            <div className={`${styles.progressStep} ${step === 'select-theirs' || step === 'select-yours' || step === 'confirm' ? styles.active : ''}`}>
              1. Want
            </div>
            <div className={`${styles.progressStep} ${step === 'select-yours' || step === 'confirm' ? styles.active : ''}`}>
              2. Offer
            </div>
            <div className={`${styles.progressStep} ${step === 'confirm' ? styles.active : ''}`}>
              3. Send
            </div>
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className={styles.success}>{success}</div>
        )}

        {/* Error message */}
        {displayError && (
          <div className={styles.error}>{displayError}</div>
        )}

        {/* Existing trade view */}
        {step === 'existing' && existingTrade && (
          <ExistingTradeView
            trade={existingTrade}
            onAccept={() => handleAcceptTrade(existingTrade.id)}
            onDecline={() => handleDeclineTrade(existingTrade.id)}
            onCounter={handleStartCounter}
            isLoading={tradeActionLoading}
          />
        )}

        {/* Step 1: Select their creature (what you want) */}
        {step === 'select-theirs' && (
          <div className={styles.content}>
            <p className={styles.instructions}>
              Select a creature you want from {targetDisplayName}:
            </p>

            {loadingTheirs ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner}></div>
                <p>Loading their creatures...</p>
              </div>
            ) : theirCreatures.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No creatures available</p>
                <p className={styles.hint}>{targetDisplayName} hasn't created any creatures yet!</p>
              </div>
            ) : (
              <div className={styles.creatureGrid}>
                {theirCreatures.map(creature => (
                  <button
                    key={creature.id}
                    className={`${styles.creatureCard} ${
                      !creature.isTradeable ? styles.unavailable : ''
                    } ${selectedTheirCreature?.id === creature.id ? styles.selected : ''}`}
                    onClick={() => handleSelectTheirCreature(creature)}
                    disabled={!creature.isTradeable}
                    title={creature.tradeableReason || creature.name}
                  >
                    {creature.menuSprite ? (
                      <img
                        src={creature.menuSprite}
                        alt={creature.name}
                        className={styles.creatureSprite}
                      />
                    ) : (
                      <div className={styles.creaturePlaceholder}>?</div>
                    )}
                    <span className={styles.creatureName}>{creature.name}</span>
                  </button>
                ))}
              </div>
            )}

            {existingTrade && (
              <button
                className={styles.backButton}
                onClick={() => setStep('existing')}
              >
                Back to Trade
              </button>
            )}
          </div>
        )}

        {/* Step 2: Select your creature (what you're offering) */}
        {step === 'select-yours' && (
          <div className={styles.content}>
            <p className={styles.instructions}>
              Select a creature to offer in exchange:
            </p>

            {/* Show what they selected */}
            {selectedTheirCreature && (
              <div className={styles.selectionPreview}>
                <span>You want:</span>
                <div className={styles.miniCard}>
                  {selectedTheirCreature.menuSprite ? (
                    <img src={selectedTheirCreature.menuSprite} alt={selectedTheirCreature.name} />
                  ) : (
                    <span>?</span>
                  )}
                  <span>{selectedTheirCreature.name}</span>
                </div>
              </div>
            )}

            {loadingYours ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner}></div>
                <p>Loading your creatures...</p>
              </div>
            ) : yourCreatures.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No creatures available</p>
                <p className={styles.hint}>Create some creatures first!</p>
              </div>
            ) : (
              <div className={styles.creatureGrid}>
                {yourCreatures.map(creature => (
                  <button
                    key={creature.id}
                    className={`${styles.creatureCard} ${
                      !creature.isTradeable ? styles.unavailable : ''
                    } ${selectedYourCreature?.id === creature.id ? styles.selected : ''}`}
                    onClick={() => handleSelectYourCreature(creature)}
                    disabled={!creature.isTradeable}
                    title={creature.tradeableReason || creature.name}
                  >
                    {creature.menuSprite ? (
                      <img
                        src={creature.menuSprite}
                        alt={creature.name}
                        className={styles.creatureSprite}
                      />
                    ) : (
                      <div className={styles.creaturePlaceholder}>?</div>
                    )}
                    <span className={styles.creatureName}>{creature.name}</span>
                  </button>
                ))}
              </div>
            )}

            <button
              className={styles.backButton}
              onClick={() => {
                setSelectedTheirCreature(null);
                setStep('select-theirs');
              }}
            >
              Back
            </button>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 'confirm' && selectedTheirCreature && selectedYourCreature && (
          <div className={styles.content}>
            <div className={styles.tradePreview}>
              {/* Your offer */}
              <div className={styles.offerSide}>
                <span className={styles.sideLabel}>You offer:</span>
                <div className={styles.offerCard}>
                  {selectedYourCreature.menuSprite ? (
                    <img
                      src={selectedYourCreature.menuSprite}
                      alt={selectedYourCreature.name}
                      className={styles.offerSprite}
                    />
                  ) : (
                    <div className={styles.offerPlaceholder}>?</div>
                  )}
                  <div className={styles.offerInfo}>
                    <span className={styles.offerName}>{selectedYourCreature.name}</span>
                    {selectedYourCreature.concept && (
                      <span className={styles.offerConcept}>{selectedYourCreature.concept}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.tradeArrow}>⇄</div>

              {/* What you want */}
              <div className={styles.offerSide}>
                <span className={styles.sideLabel}>You want:</span>
                <div className={styles.offerCard}>
                  {selectedTheirCreature.menuSprite ? (
                    <img
                      src={selectedTheirCreature.menuSprite}
                      alt={selectedTheirCreature.name}
                      className={styles.offerSprite}
                    />
                  ) : (
                    <div className={styles.offerPlaceholder}>?</div>
                  )}
                  <div className={styles.offerInfo}>
                    <span className={styles.offerName}>{selectedTheirCreature.name}</span>
                    {selectedTheirCreature.concept && (
                      <span className={styles.offerConcept}>{selectedTheirCreature.concept}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.confirmActions}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setSelectedYourCreature(null);
                  setStep('select-yours');
                }}
              >
                Back
              </button>
              <button
                className={styles.confirmButton}
                onClick={existingTrade ? () => handleCounterTrade(existingTrade.id) : handleConfirmTrade}
                disabled={tradeActionLoading}
              >
                {tradeActionLoading
                  ? 'Sending...'
                  : existingTrade
                    ? 'Send Counter'
                    : 'Send Offer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Existing trade view sub-component
 */
interface ExistingTradeViewProps {
  trade: TradeNotification;
  onAccept: () => void;
  onDecline: () => void;
  onCounter: () => void;
  isLoading: boolean;
}

function ExistingTradeView({
  trade,
  onAccept,
  onDecline,
  onCounter,
  isLoading
}: ExistingTradeViewProps) {
  const countdown = useTradeCountdown(trade.expiresAt);

  return (
    <div className={styles.existingTrade}>
      <div className={styles.tradeStatus}>
        <span className={styles.statusLabel}>
          {trade.isYourTurn ? 'Your turn to respond' : 'Waiting for response'}
        </span>
        <span className={styles.countdown}>Expires: {countdown}</span>
      </div>

      <div className={styles.tradeOffers}>
        {/* Your offer */}
        <div className={styles.offerSide}>
          <span className={styles.sideLabel}>You offer:</span>
          <div className={styles.offerCard}>
            {trade.yourOffer.creatureSprite ? (
              <img
                src={trade.yourOffer.creatureSprite}
                alt={trade.yourOffer.creatureName}
                className={styles.offerSprite}
              />
            ) : (
              <div className={styles.offerPlaceholder}>?</div>
            )}
            <span className={styles.offerName}>{trade.yourOffer.creatureName}</span>
          </div>
        </div>

        <div className={styles.tradeArrow}>⇄</div>

        {/* Their offer */}
        <div className={styles.offerSide}>
          <span className={styles.sideLabel}>{trade.withUser.displayName} offers:</span>
          <div className={styles.offerCard}>
            {trade.theirOffer.creatureSprite ? (
              <img
                src={trade.theirOffer.creatureSprite}
                alt={trade.theirOffer.creatureName}
                className={styles.offerSprite}
              />
            ) : (
              <div className={styles.offerPlaceholder}>?</div>
            )}
            <span className={styles.offerName}>{trade.theirOffer.creatureName}</span>
          </div>
        </div>
      </div>

      {trade.counterCount > 0 && (
        <div className={styles.counterInfo}>
          Counter-offers: {trade.counterCount}/5
        </div>
      )}

      {trade.isYourTurn && (
        <div className={styles.tradeActions}>
          <button
            className={styles.declineButton}
            onClick={onDecline}
            disabled={isLoading}
          >
            Decline
          </button>
          <button
            className={styles.counterButton}
            onClick={onCounter}
            disabled={isLoading || trade.counterCount >= 5}
            title={trade.counterCount >= 5 ? 'Max counter-offers reached' : 'Offer different creature'}
          >
            Counter
          </button>
          <button
            className={styles.acceptButton}
            onClick={onAccept}
            disabled={isLoading}
          >
            Accept
          </button>
        </div>
      )}

      {!trade.isYourTurn && (
        <div className={styles.waitingMessage}>
          Waiting for {trade.withUser.displayName} to respond...
        </div>
      )}
    </div>
  );
}
